import { configService } from './configService';

const TOKEN_KEY = 'ariadifest_admin_token';

export interface MenuOpzioneValore {
  key: string;
  label: string;
}

export interface MenuOpzione {
  key: string;
  label: string;
  obbligatoria: boolean;
  valori: MenuOpzioneValore[];
}

export interface MenuCategoria {
  key: string;
  label: string;
  prezzo: number;
  opzioni: MenuOpzione[];
}

export interface MenuConfig {
  _id: string;
  edizione: string;
  attivo: boolean;
  accontoPerPersona: number;
  categorie: MenuCategoria[];
  bicchiereCommemorativo?: {
    attivo: boolean;
    label: string;
    prezzo: number;
  };
}

export interface MenuPartecipante {
  categoria: string;
  selezioni: Record<string, string>;
  prezzoUnitario: number;
  bicchiereCommemorativo?: boolean;
  prezzoBicchiere?: number;
}

export interface Registration {
  _id: string;
  bookingNumber: string;
  email: string;
  nome: string;
  cognome: string;
  telefono: string;
  nomeGruppo: string;
  note: string;
  numeroPartecipanti: number;
  menuConfigId: string;
  menuPartecipanti: MenuPartecipante[];
  totaleDovuto: number;
  importoPagato: number;
  saldoRimanente: number;
  payment: {
    tipo: string;
    orderId: string;
    payerId: string;
    payerEmail: string;
    payerName: string;
  };
  numeroTavolo: string;
  emailSent: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminStats {
  totali: {
    totaleRegistrazioni: number;
    totalePartecipanti: number;
    totaleDovuto: number;
    totalePagato: number;
    totaleRimanente: number;
  };
  perCategoria: Array<{ _id: string; count: number; incasso: number }>;
  perOpzione: Array<{ _id: { chiave: string; valore: string }; count: number }>;
  perTipoPagamento: Array<{ _id: string; count: number }>;
  bicchieriCommemorativi: { count: number; incasso: number };
}

export type RegistrationEditableFields = Partial<
  Pick<Registration, 'email' | 'nome' | 'cognome' | 'telefono' | 'nomeGruppo' | 'note' | 'numeroTavolo'>
>;

class AdminApiService {
  private getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private setToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  private clearToken() {
    localStorage.removeItem(TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  logout() {
    this.clearToken();
  }

  async login(username: string, password: string): Promise<void> {
    const backendUrl = configService.getBackendUrl();
    const res = await fetch(`${backendUrl}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Credenziali non valide');
    }
    this.setToken(data.token);
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const backendUrl = configService.getBackendUrl();
    const token = this.getToken();

    const res = await fetch(`${backendUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {})
      }
    });

    if (res.status === 401) {
      this.clearToken();
      throw new Error('Sessione scaduta, effettua di nuovo il login');
    }

    const data = await res.json();
    if (!res.ok || data.success === false) {
      throw new Error(data.message || 'Errore nella richiesta');
    }
    return data;
  }

  async getRegistrations(): Promise<Registration[]> {
    const data = await this.request<{ success: boolean; data: Registration[] }>('/api/admin/registrations');
    return data.data;
  }

  async getMenuConfig(): Promise<MenuConfig> {
    const data = await this.request<{ success: boolean; data: MenuConfig }>('/api/admin/menu-config');
    return data.data;
  }

  async getStats(): Promise<AdminStats> {
    const data = await this.request<{ success: boolean; data: AdminStats }>('/api/admin/stats');
    return data.data;
  }

  async updateRegistration(bookingNumber: string, updates: RegistrationEditableFields): Promise<Registration> {
    const data = await this.request<{ success: boolean; data: Registration }>(
      `/api/admin/registrations/${encodeURIComponent(bookingNumber)}`,
      { method: 'PUT', body: JSON.stringify(updates) }
    );
    return data.data;
  }

  async updateRegistrationPayment(bookingNumber: string, updates: { totaleDovuto?: number; importoPagato?: number }): Promise<Registration> {
    const data = await this.request<{ success: boolean; data: Registration }>(
      `/api/admin/registrations/${encodeURIComponent(bookingNumber)}/payment`,
      { method: 'PUT', body: JSON.stringify(updates) }
    );
    return data.data;
  }

  async deleteRegistration(bookingNumber: string): Promise<void> {
    await this.request(`/api/admin/registrations/${encodeURIComponent(bookingNumber)}`, {
      method: 'DELETE'
    });
  }
}

export const adminApiService = new AdminApiService();
export default adminApiService;
